import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Medication } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { saveMedicationTemplate, updateMedicationTemplate } from "@/lib/stores/medicationTemplates";
import { useAuthStore } from "@/lib/auth";
import { getDoctorIdForUser, ensureDoctorProfile } from "@/lib/utils/doctorUtils";
import { MedicationForm } from "./MedicationForm";
import { MedicationTemplate } from "@/types";

interface SaveTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  medications: Medication[];
  onTemplateSaved?: () => void;
  allowEdit?: boolean; // Allow editing medications in the dialog
  editingTemplate?: MedicationTemplate | null; // Template being edited
}

export function SaveTemplateDialog({
  isOpen,
  onClose,
  medications: initialMedications,
  onTemplateSaved,
  allowEdit = false,
  editingTemplate = null
}: SaveTemplateDialogProps) {
  const { toast } = useToast();
  const { user } = useAuthStore();
  const [diagnosis, setDiagnosis] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [medications, setMedications] = useState<Medication[]>(initialMedications);
  
  // Update form when dialog opens or when editing template changes
  useEffect(() => {
    if (isOpen) {
      if (editingTemplate) {
        // Pre-fill form with template data
        setDiagnosis(editingTemplate.diagnosis || "");
        setTemplateName(editingTemplate.templateName || "");
        let templateMedications: Medication[] = [];
        try {
          if (typeof editingTemplate.medications === 'string') {
            templateMedications = JSON.parse(editingTemplate.medications);
          } else if (Array.isArray(editingTemplate.medications)) {
            templateMedications = editingTemplate.medications;
          }
        } catch (e) {
          console.error('Error parsing medications:', e);
        }
        setMedications(templateMedications);
      } else {
        // Reset form for new template
        setDiagnosis("");
        setTemplateName("");
        setMedications(initialMedications);
      }
    }
  }, [isOpen, editingTemplate, initialMedications]);
  
  // Determine if we should show the medication form (when editing or when no medications provided)
  const showMedicationForm = allowEdit || medications.length === 0;

  const handleSave = async () => {
    if (!diagnosis.trim()) {
      toast({
        title: "Diagnosis required",
        description: "Please enter a diagnosis for the template",
        variant: "destructive"
      });
      return;
    }

    if (!templateName.trim()) {
      toast({
        title: "Template name required",
        description: "Please enter a name for the template",
        variant: "destructive"
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: "Authentication error",
        description: "User ID not found",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);
    try {
      // Get or create doctor profile for the user
      let doctorId = await getDoctorIdForUser(user);
      
      if (!doctorId) {
        console.log('No doctor profile found, creating one...');
        doctorId = await ensureDoctorProfile(user);
        
        if (!doctorId) {
          toast({
            title: "Error",
            description: "Could not create doctor profile. Please contact administrator.",
            variant: "destructive"
          });
          return;
        }
      }

      console.log('Using doctor ID:', doctorId);

      if (editingTemplate) {
        // Update existing template
        await updateMedicationTemplate(editingTemplate.id, {
          diagnosis: diagnosis.trim(),
          templateName: templateName.trim(),
          medications: medications
        });

        toast({
          title: "Template updated",
          description: `Template "${templateName}" updated successfully`
        });
      } else {
        // Create new template
        await saveMedicationTemplate({
          doctorId: doctorId,
          diagnosis: diagnosis.trim(),
          templateName: templateName.trim(),
          medications: medications
        });

        toast({
          title: "Template saved",
          description: `Template "${templateName}" saved successfully for ${diagnosis}`
        });
      }

      // Reset form
      setDiagnosis("");
      setTemplateName("");
      setMedications([]);
      onTemplateSaved?.();
      onClose();
    } catch (error) {
      console.error("Error saving template:", error);
      toast({
        title: "Error saving template",
        description: "Failed to save template. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setDiagnosis("");
    setTemplateName("");
    setMedications([]);
    onClose();
  };

  const handleAddMedication = (medication: Medication) => {
    setMedications([...medications, medication]);
  };

  const handleAddMultipleMedications = (newMedications: Medication[]) => {
    setMedications([...medications, ...newMedications]);
  };

  const handleRemoveMedication = (index: number) => {
    setMedications(medications.filter((_, i) => i !== index));
  };

  const handleUpdateMedication = (medication: Medication, index: number) => {
    const updated = [...medications];
    updated[index] = medication;
    setMedications(updated);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingTemplate 
              ? "Edit Template" 
              : showMedicationForm 
                ? "Create New Template" 
                : "Save as Template"}
          </DialogTitle>
          <DialogDescription>
            {editingTemplate
              ? "Update the medication template details below."
              : showMedicationForm 
                ? "Create a new medication template with diagnosis, name, and medications."
                : "Save your current medications as a reusable template for future prescriptions."}
          </DialogDescription>
        </DialogHeader>
        
        {showMedicationForm ? (
          // Full form view when editing or creating new template
          <div className="space-y-6">
            {/* Diagnosis and Template Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="diagnosis">Diagnosis *</Label>
                <Input
                  id="diagnosis"
                  value={diagnosis}
                  onChange={(e) => setDiagnosis(e.target.value)}
                  placeholder="e.g., ASOM, SINUSITIS, ADULT ACUTE TONSILLITIS"
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="template-name">Template Name *</Label>
                <Input
                  id="template-name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., Standard ASOM Treatment"
                  className="w-full"
                />
              </div>
            </div>

            {/* Medication Form */}
            <div className="border rounded-md p-4">
              <MedicationForm
                medications={medications}
                onAddMedication={handleAddMedication}
                onAddMultipleMedications={handleAddMultipleMedications}
                onRemoveMedication={handleRemoveMedication}
                onUpdateMedication={handleUpdateMedication}
              />
            </div>
          </div>
        ) : (
          // Preview view when medications are provided
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left side - Diagnosis and Template Name */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="diagnosis">Diagnosis</Label>
              <Input
                id="diagnosis"
                value={diagnosis}
                onChange={(e) => setDiagnosis(e.target.value)}
                placeholder="e.g., ASOM, SINUSITIS, ADULT ACUTE TONSILLITIS"
                className="w-full"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Standard ASOM Treatment"
                className="w-full"
              />
            </div>

            <div className="text-sm text-muted-foreground">
              <p>This template will be saved to your personal template library and can be used for future patients with similar diagnoses.</p>
            </div>
          </div>

          {/* Right side - Medications Preview */}
          <div className="space-y-4">
            <Label>Medications to Save ({medications.length})</Label>
            <div className="border rounded-md p-4 max-h-96 overflow-y-auto">
              {medications.length === 0 ? (
                <p className="text-sm text-muted-foreground">No medications to save</p>
              ) : (
                <div className="space-y-3">
                  {medications.map((medication, index) => (
                    <div key={index} className="border-b border-gray-100 pb-3 last:border-b-0">
                      <div className="font-medium text-sm">
                        {index + 1}. {medication.name}
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <div>Dosage: {medication.dosage} {medication.doseType}</div>
                        <div>Frequency: {medication.frequency}</div>
                        {medication.frequencyPattern && (
                          <div>Pattern: {medication.frequencyPattern}</div>
                        )}
                        <div>Duration: {medication.duration} days</div>
                        {medication.instructions && (
                          <div>Instructions: {medication.instructions}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isSaving || medications.length === 0}
            className="min-w-[120px]"
          >
            {isSaving ? (editingTemplate ? "Updating..." : "Saving...") : (editingTemplate ? "Update Template" : "Save Template")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
